import { useState } from 'react';
import { IngredientParser } from '@core/parser';
import { FeedbackCollector } from '@core/feedback';

const EXAMPLE_INGREDIENTS = [
  '2 cups all-purpose flour',
  '1 1/2 lbs ground beef',
  '½ cup sugar',
  '2-3 tablespoons butter',
  'about 1 cup milk',
  'salt to taste',
  '1 (14.5 oz) can diced tomatoes',
  '2 cups chopped onions',
  '3 cloves garlic, minced',
  'one 8-ounce package cream cheese',
];

interface ParseDemoProps {
  collector: FeedbackCollector;
}

export function ParseDemo({ collector }: ParseDemoProps) {
  const [input, setInput] = useState('2 cups all-purpose flour');
  const [parser] = useState(() => new IngredientParser());
  const [feedbackMode, setFeedbackMode] = useState<'confirm' | 'correct' | null>(null);
  const [correctionInput, setCorrectionInput] = useState({
    quantity: '',
    unit: '',
    ingredient: '',
  });

  const result = parser.parse(input);

  const handleConfirm = () => {
    collector.recordConfirmation(input, result);
    setFeedbackMode('confirm');
    setTimeout(() => setFeedbackMode(null), 2000);
  };

  const handleCorrect = () => {
    const correction: any = {};
    if (correctionInput.quantity) correction.quantity = parseFloat(correctionInput.quantity);
    if (correctionInput.unit) correction.unit = correctionInput.unit;
    if (correctionInput.ingredient) correction.ingredient = correctionInput.ingredient;

    if (Object.keys(correction).length > 0) {
      collector.recordCorrection(input, result, correction);
      setFeedbackMode('correct');
      setCorrectionInput({ quantity: '', unit: '', ingredient: '' });
      setTimeout(() => setFeedbackMode(null), 2000);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          Ingredient Parser Demo
        </h2>
        <p className="text-sm text-gray-600 mb-6">
          Type any ingredient or select from examples below. Watch the parser extract quantities, units, and ingredient names in real-time.
        </p>

        {/* Input */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Ingredient Line
            </label>
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g., 2 cups flour"
            />
          </div>

          {/* Examples */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Quick Examples
            </label>
            <div className="flex flex-wrap gap-2">
              {EXAMPLE_INGREDIENTS.map((example, i) => (
                <button
                  key={i}
                  onClick={() => setInput(example)}
                  className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md"
                >
                  {example}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Parsed Output */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Parsed Result</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Quantity */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Quantity</h4>
            <div className="bg-blue-50 rounded-md p-4">
              {result.quantity ? (
                <div className="space-y-2">
                  <div>
                    <span className="text-xs text-gray-600">Value:</span>
                    <div className="text-lg font-semibold text-blue-900">
                      {result.quantity.value}
                    </div>
                  </div>
                  <div>
                    <span className="text-xs text-gray-600">Type:</span>
                    <div className="text-sm text-blue-700">
                      {result.quantity.type}
                    </div>
                  </div>
                  {result.quantity.type === 'range' && (
                    <div>
                      <span className="text-xs text-gray-600">Range:</span>
                      <div className="text-sm text-blue-700">
                        {result.quantity.valueLow} - {result.quantity.valueHigh}
                      </div>
                    </div>
                  )}
                  <div>
                    <span className="text-xs text-gray-600">Display:</span>
                    <div className="text-sm font-mono text-blue-700">
                      {result.quantity.display}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-gray-400 italic">No quantity</div>
              )}
            </div>
          </div>

          {/* Unit */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Unit</h4>
            <div className="bg-green-50 rounded-md p-4">
              {result.unit ? (
                <div className="space-y-2">
                  <div>
                    <span className="text-xs text-gray-600">Canonical:</span>
                    <div className="text-lg font-semibold text-green-900">
                      {result.unit.canonical}
                    </div>
                  </div>
                  <div>
                    <span className="text-xs text-gray-600">Type:</span>
                    <div className="text-sm text-green-700">
                      {result.unit.type}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-gray-400 italic">No unit</div>
              )}
            </div>
          </div>

          {/* Ingredient */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Ingredient</h4>
            <div className="bg-purple-50 rounded-md p-4">
              <div className="space-y-2">
                <div>
                  <span className="text-xs text-gray-600">Canonical:</span>
                  <div className="text-lg font-semibold text-purple-900">
                    {result.ingredient.canonical || 'unknown'}
                  </div>
                </div>
                <div>
                  <span className="text-xs text-gray-600">Category:</span>
                  <div className="text-sm text-purple-700">
                    {result.ingredient.category}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Metadata */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Additional Info</h4>
            <div className="bg-gray-50 rounded-md p-4 space-y-2">
              <div>
                <span className="text-xs text-gray-600">Confidence:</span>
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${
                        result.confidence > 0.9
                          ? 'bg-green-500'
                          : result.confidence > 0.7
                          ? 'bg-yellow-500'
                          : 'bg-red-500'
                      }`}
                      style={{ width: `${result.confidence * 100}%` }}
                    />
                  </div>
                  <span className="text-sm font-semibold">
                    {(result.confidence * 100).toFixed(0)}%
                  </span>
                </div>
              </div>

              {result.preparation.length > 0 && (
                <div>
                  <span className="text-xs text-gray-600">Preparation:</span>
                  <div className="text-sm text-gray-900">
                    {result.preparation.join(', ')}
                  </div>
                </div>
              )}

              {result.modifiers.length > 0 && (
                <div>
                  <span className="text-xs text-gray-600">Modifiers:</span>
                  <div className="text-sm text-gray-900">
                    {result.modifiers.join(', ')}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Flags */}
        <div className="mt-6 border-t border-gray-200 pt-4">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Parsing Flags</h4>
          <div className="flex flex-wrap gap-2">
            {Object.entries(result.flags).map(([key, value]) => (
              <span
                key={key}
                className={`px-3 py-1 text-xs rounded-full ${
                  value
                    ? 'bg-blue-100 text-blue-800'
                    : 'bg-gray-100 text-gray-500'
                }`}
              >
                {key}: {value ? '✓' : '✗'}
              </span>
            ))}
          </div>
        </div>

        {/* JSON Output */}
        <div className="mt-6 border-t border-gray-200 pt-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">JSON Output</h4>
          <pre className="bg-gray-900 text-gray-100 text-xs p-4 rounded-md overflow-x-auto">
            {JSON.stringify(result, null, 2)}
          </pre>
        </div>
      </div>

      {/* Feedback Section */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Help Improve the Parser
        </h3>
        <p className="text-sm text-gray-600 mb-6">
          Was this parsing correct? Your feedback helps improve accuracy.
        </p>

        {/* Feedback Status */}
        {feedbackMode && (
          <div className={`mb-4 p-4 rounded-md ${
            feedbackMode === 'confirm' ? 'bg-green-50' : 'bg-blue-50'
          }`}>
            <p className={`text-sm font-medium ${
              feedbackMode === 'confirm' ? 'text-green-800' : 'text-blue-800'
            }`}>
              {feedbackMode === 'confirm'
                ? '✓ Thank you! Confirmation recorded.'
                : '✓ Thank you! Correction recorded.'}
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Confirm */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-gray-700">Parsing is Correct</h4>
            <button
              onClick={handleConfirm}
              className="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              ✓ Confirm Correct
            </button>
          </div>

          {/* Correct */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-gray-700">Submit Correction</h4>
            <div className="space-y-2">
              <input
                type="text"
                value={correctionInput.quantity}
                onChange={(e) => setCorrectionInput({ ...correctionInput, quantity: e.target.value })}
                placeholder="Correct quantity (e.g., 2.5)"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
              <input
                type="text"
                value={correctionInput.unit}
                onChange={(e) => setCorrectionInput({ ...correctionInput, unit: e.target.value })}
                placeholder="Correct unit (e.g., cup)"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
              <input
                type="text"
                value={correctionInput.ingredient}
                onChange={(e) => setCorrectionInput({ ...correctionInput, ingredient: e.target.value })}
                placeholder="Correct ingredient (e.g., all-purpose flour)"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
              <button
                onClick={handleCorrect}
                disabled={!correctionInput.quantity && !correctionInput.unit && !correctionInput.ingredient}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Submit Correction
              </button>
            </div>
          </div>
        </div>

        <div className="mt-6 pt-4 border-t border-gray-200">
          <p className="text-xs text-gray-500">
            Events recorded: {collector.getAllEvents().length} • View detailed analytics in the Analytics tab
          </p>
        </div>
      </div>
    </div>
  );
}
